import React, { useState, useEffect } from "react";
import { Button, Input, message, Upload } from "antd";
import { MdOutlineNavigateNext } from "react-icons/md";
import { PlusOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import axios from "axios";
import "react-quill/dist/quill.snow.css";
import { useDrawer } from "../../../../context/drawer";

export default function UpdateBlogs() {
  const [loading, setLoading] = useState(false);
  const { drawerData, toggleDrawer } = useDrawer();

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    short_description: "",
    main_description: "",
    meta_title: "",
    meta_description: "",
    canonical_tag: "",
  });

  const [fileList, setFileList] = useState([]);
  const [initialImageExists, setInitialImageExists] = useState(false);

  useEffect(() => {
    if (drawerData?.data) {
      const blogData = drawerData.data;
      setFormData({
        title: blogData.title || "",
        slug: blogData.slug || "",
        short_description: blogData.short_description || "",
        main_description: blogData.content || "",
        meta_title: blogData.meta_title || "",
        meta_description: blogData.meta_description || "",
        canonical_tag: blogData.canonical_tag || "",
      });

      if (blogData.image) {
        let imageUrl = blogData.image;
        if (imageUrl.startsWith("/9j/") && !imageUrl.startsWith("data:image")) {
          imageUrl = `data:image/jpeg;base64,${imageUrl}`;
        }
        const existingFile = {
          uid: blogData.id || blogData.blog_id || "-current",
          name: "current_image.jpg",
          status: "done",
          url: imageUrl,
        };
        setFileList([existingFile]);
        setInitialImageExists(true);
      } else {
        setFileList([]);
        setInitialImageExists(false);
      }
    } else {
      setFormData({
        title: "",
        slug: "",
        short_description: "",
        main_description: "",
        meta_title: "",
        meta_description: "",
        canonical_tag: "",
      });
      setFileList([]);
      setInitialImageExists(false);
    }

    return () => {
      setLoading(false);
      setFormData({
        title: "",
        slug: "",
        short_description: "",
        main_description: "",
        meta_title: "",
        meta_description: "",
        canonical_tag: "",
      });
      fileList.forEach(file => {
        if (file.url?.startsWith("blob:")) {
          URL.revokeObjectURL(file.url);
        }
      });
      setFileList([]);
      setInitialImageExists(false);
    };
  }, [drawerData.data, drawerData.type]);

  useEffect(() => {
    if (drawerData.type !== "blogEdit" || (formData.slug === "" && formData.title !== "")) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.title, drawerData.type, formData.slug]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmitClick = async () => {
    const { title, slug, short_description, main_description } = formData;
    const isEditing = drawerData.type === "blogEdit" && drawerData.data;
    const blogId = isEditing ? (drawerData.data.id || drawerData.data.blog_id) : null;

    if (!title || !slug || !short_description || !main_description) {
      message.warning("Please fill all required fields.");
      return;
    }

    if (!isEditing && fileList.length === 0) {
      message.warning("Please upload an image for the new blog.");
      return;
    }

    if (isEditing && !initialImageExists && fileList.length === 0) {
      message.warning("Please upload an image for this blog.");
      return;
    }

    try {
      setLoading(true);
      const data = new FormData();

      if (isEditing) {
        data.append("action", "update_blog");
        data.append("id", blogId);
      } else {
        data.append("action", "add_blog");
      }

      data.append("title", title);
      data.append("slug", slug);
      data.append("short_description", short_description);
      data.append("content", main_description);
      data.append("meta_title", formData.meta_title || "");
      data.append("meta_description", formData.meta_description || "");
      data.append("canonical_tag", formData.canonical_tag || "");

      if (fileList.length > 0 && fileList[0].originFileObj) {
        data.append("image", fileList[0].originFileObj);
        data.append("image_updated", "true");
      } else if (isEditing && fileList.length === 0 && initialImageExists) {
        data.append("remove_image", "true");
        data.append("image_updated", "true");
      } else if (isEditing && fileList.length > 0 && !fileList[0].originFileObj) {
        data.append("image_updated", "false");
      } else {
        data.append("image_updated", "false");
      }

      const resp = await axios.post("https://skdm.in/server/sdg/blogs.php", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (resp?.data?.status === "success" || resp.status === 200) {
        message.success(`Blog ${isEditing ? "updated" : "added"} successfully!`);
        toggleDrawer();
        if (drawerData?.onSuccess) {
          drawerData.onSuccess();
        }
      } else {
        message.error(resp?.data?.message || `Server error: Blog not ${isEditing ? "updated" : "added"}.`);
      }
    } catch (err) {
      message.error(`Failed to ${isEditing ? "update" : "add"} blog. Details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-blog-form">
      <label>Title</label>
      <Input
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        placeholder="Enter blog title"
      />

      <label className="mt-3">Slug</label>
      <Input
        name="slug"
        value={formData.slug}
        onChange={handleInputChange}
        placeholder="Custom slug"
      />

      <label className="mt-3">Short Description</label>
      <Input.TextArea
        rows={4}
        name="short_description"
        value={formData.short_description}
        onChange={handleInputChange}
        placeholder="Enter short description"
      />

      <label className="mt-3">Main Description</label>
      <ReactQuill
        value={formData.main_description}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, main_description: value }))
        }
        theme="snow"
        style={{ minHeight: 200 }}
      />

      <label className="mt-3 d-block">Image</label>
      <Upload
        listType="picture-card"
        maxCount={1}
        fileList={fileList}
        onRemove={() => setFileList([])}
        beforeUpload={(file) => {
          const isImage = file.type.startsWith("image/");
          if (!isImage) {
            message.error("Only image files are allowed!");
            return Upload.LIST_IGNORE;
          }

          const isLt2M = file.size / 1024 / 1024 < 2;
          if (!isLt2M) {
            message.error("Image must be smaller than 2MB!");
            return Upload.LIST_IGNORE;
          }

          const newFile = {
            uid: Date.now().toString(),
            name: file.name,
            status: "done",
            originFileObj: file,
            url: URL.createObjectURL(file),
          };

          setFileList([newFile]);
          return false;
        }}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
        }}
      >
        {fileList.length === 0 && (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        )}
      </Upload>

      <hr className="my-4" />
      <h4>SEO Information</h4>

      <label className="mt-3">Meta Title</label>
      <Input
        name="meta_title"
        value={formData.meta_title}
        onChange={handleInputChange}
        placeholder="Enter meta title for SEO"
      />

      <label className="mt-3">Meta Description</label>
      <Input.TextArea
        rows={4}
        name="meta_description"
        value={formData.meta_description}
        onChange={handleInputChange}
        placeholder="Enter meta description for SEO"
      />

      <label className="mt-3">Canonical Tag</label>
      <Input
        name="canonical_tag"
        value={formData.canonical_tag}
        onChange={handleInputChange}
        placeholder="Enter canonical URL"
      />

      <div className="buttonGroup w-100 d-flex justify-content-end mt-4">
        <Button
          className="otherButtons active"
          icon={<MdOutlineNavigateNext />}
          onClick={handleSubmitClick}
          loading={loading}
        >
          {drawerData.type === "blogEdit" ? "Update Blog" : "Add Blog"}
        </Button>
      </div>
    </div>
  );
}
